'use client';
import React from 'react';
import type { PlacedComponent } from './circuitTypes';
import { COMPONENT_DEFINITIONS, WIRE_COLOR_MAP } from './circuitTypes';

interface WireInProgressRef {
  fromComponentId: string;
}

interface Props {
  component: PlacedComponent;
  isSelected: boolean;
  isLit: boolean;
  simulationMode: boolean;
  wireInProgress: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onTerminalClick: (compId: string, termIdx: number, absX: number, absY: number) => void;
  onDelete: () => void;
  onToggleSwitch: () => void;
}

const COMPONENT_COLORS: Record<string, { fill: string; stroke: string; text: string }> = {
  supply: { fill: '#dbeafe', stroke: '#3b82f6', text: '#1d4ed8' },
  lamp: { fill: '#fef9c3', stroke: '#ca8a04', text: '#92400e' },
  'switch-one-way': { fill: '#dcfce7', stroke: '#16a34a', text: '#15803d' },
  'switch-two-way': { fill: '#d1fae5', stroke: '#059669', text: '#065f46' },
  socket: { fill: '#ede9fe', stroke: '#7c3aed', text: '#5b21b6' },
  'junction-box': { fill: '#e0f2fe', stroke: '#0284c7', text: '#0369a1' },
  mcb: { fill: '#fef3c7', stroke: '#d97706', text: '#92400e' },
  rcbo: { fill: '#fde68a', stroke: '#b45309', text: '#78350f' },
  fuse: { fill: '#fee2e2', stroke: '#dc2626', text: '#991b1b' },
  'neutral-bar': { fill: '#dbeafe', stroke: '#2563eb', text: '#1d4ed8' },
  'earth-bar': { fill: '#dcfce7', stroke: '#16a34a', text: '#15803d' },
};

export default function CanvasComponent({
  component, isSelected, isLit, simulationMode,
  wireInProgress, onMouseDown, onTerminalClick, onDelete, onToggleSwitch,
}: Props) {
  const def = COMPONENT_DEFINITIONS[component.type];
  const colors = COMPONENT_COLORS[component.type] || { fill: '#f1f5f9', stroke: '#64748b', text: '#334155' };
  const { x, y, type, label, switchState } = component;
  const w = def.width;
  const h = def.height;

  const isSwitch = type === 'switch-one-way' || type === 'switch-two-way';
  const isLamp = type === 'lamp';

  const handleTerminalMouseDown = (e: React.MouseEvent, idx: number) => {
    e.stopPropagation();
    const t = def.terminals[idx];
    onTerminalClick(component.instanceId, idx, x + t.relX, y + t.relY);
  };

  return (
    <g
      className="canvas-component"
      onMouseDown={onMouseDown}
      transform={`translate(0,0)`}
    >
      {/* Selection ring */}
      {isSelected && (
        <rect
          x={x - 5}
          y={y - 5}
          width={w + 10}
          height={h + 10}
          rx={10}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="1.5"
          strokeDasharray="5 3"
          className="pointer-events-none"
        />
      )}

      {/* Component body */}
      {isLamp ? (
        <>
          {/* Glow halo behind the bulb when lit */}
          {isLit && (
            <circle
              cx={x + w / 2}
              cy={y + h / 2}
              r={Math.min(w, h) / 2 + 10}
              fill="#fef08a"
              opacity="0.35"
              className="pointer-events-none"
            />
          )}
          <circle
            cx={x + w / 2}
            cy={y + h / 2}
            r={Math.min(w, h) / 2 - 2}
            fill={isLit ? '#fde047' : colors.fill}
            stroke={isLit ? '#f59e0b' : colors.stroke}
            strokeWidth={isLit ? 2.5 : 1.5}
            className="component-body transition-all duration-300"
          />
          {/* Animated outer ring when lit */}
          {isLit && (
            <circle
              cx={x + w / 2}
              cy={y + h / 2}
              r={Math.min(w, h) / 2 + 5}
              fill="none"
              stroke="#fbbf24"
              strokeWidth="2"
              opacity="0.6"
              className="pointer-events-none animate-ping"
            />
          )}
          {/* Filament lines */}
          <line
            x1={x + w / 2 - 8}
            y1={y + h / 2}
            x2={x + w / 2 + 8}
            y2={y + h / 2}
            stroke={isLit ? '#92400e' : colors.stroke}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <line
            x1={x + w / 2}
            y1={y + h / 2 - 8}
            x2={x + w / 2}
            y2={y + h / 2 + 8}
            stroke={isLit ? '#92400e' : colors.stroke}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </>
      ) : isSwitch ? (
        <>
          <rect
            x={x}
            y={y}
            width={w}
            height={h}
            rx={6}
            fill={switchState && simulationMode ? '#bbf7d0' : colors.fill}
            stroke={switchState && simulationMode ? '#16a34a' : colors.stroke}
            strokeWidth="1.5"
            className="component-body transition-all duration-200"
          />
          {/* Switch lever line */}
          <line
            x1={x + 10}
            y1={y + h / 2}
            x2={x + w - 10}
            y2={switchState ? y + h / 2 : y + h / 2 - 8}
            stroke={colors.stroke}
            strokeWidth="2"
            strokeLinecap="round"
          />
          {simulationMode && (
            <rect
              x={x}
              y={y}
              width={w}
              height={h}
              rx={6}
              fill="transparent"
              stroke="transparent"
              className="cursor-pointer"
              onClick={(e) => { e.stopPropagation(); onToggleSwitch(); }}
            />
          )}
        </>
      ) : (
        <rect
          x={x}
          y={y}
          width={w}
          height={h}
          rx={6}
          fill={colors.fill}
          stroke={colors.stroke}
          strokeWidth="1.5"
          className="component-body"
        />
      )}

      {/* Label */}
      <text
        x={x + w / 2}
        y={isLamp ? y + h + 14 : y + h / 2 + 4}
        textAnchor="middle"
        fontSize="10"
        fontWeight="600"
        fill={colors.text}
        className="pointer-events-none select-none font-mono"
      >
        {label}
      </text>

      {/* Type label for non-lamp */}
      {!isLamp && (
        <text
          x={x + w / 2}
          y={y + h / 2 - 6}
          textAnchor="middle"
          fontSize="8"
          fontWeight="500"
          fill={colors.text}
          opacity="0.7"
          className="pointer-events-none select-none"
        >
          {def.label}
        </text>
      )}

      {/* Terminals */}
      {def.terminals.map((t, idx) => {
        const termColor = WIRE_COLOR_MAP[t.color].hex;
        return (
          <g key={`${component.instanceId}-term-${idx}`}>
            <circle
              cx={x + t.relX}
              cy={y + t.relY}
              r={6}
              fill="white"
              stroke={termColor}
              strokeWidth="2"
              className="terminal-dot"
              onMouseDown={(e) => handleTerminalMouseDown(e, idx)}
              style={{ cursor: 'crosshair' }}
            />
            <text
              x={x + t.relX}
              y={y + t.relY - 9}
              textAnchor="middle"
              fontSize="7"
              fontWeight="600"
              fill={termColor}
              className="pointer-events-none select-none font-mono"
            >
              {t.label}
            </text>
          </g>
        );
      })}

      {/* Delete button — shown when selected */}
      {isSelected && (
        <g>
          <circle
            cx={x + w + 2}
            cy={y - 2}
            r={8}
            fill="#ef4444"
            stroke="white"
            strokeWidth="1.5"
            className="cursor-pointer hover:fill-red-600 transition-colors"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          />
          <text
            x={x + w + 2}
            y={y + 2}
            textAnchor="middle"
            fontSize="10"
            fontWeight="700"
            fill="white"
            className="pointer-events-none select-none"
          >
            ×
          </text>
        </g>
      )}
    </g>
  );
}